// Wizard-only department/user parsers, and Personio's employee export for the
// departments, users and teams steps. The shared read/normalize/validate core
// (`buildRows`) and the cross-surface parsers (org-functions, systems, memberships)
// live in `@/lib/excel`; these stay here because they depend on the wizard's
// local row schemas.
import type * as XLSX from 'xlsx';
import {
  abbreviationInitials,
  canonicalEmail,
  claimAbbreviation,
  type OrgFunctionKind,
  type PersonioSetupUnsuppliedPerson,
  type ShowPersonioSetupRowsResponse,
  unsuppliedReasons
} from '@bliks-io/contracts';
import {
  buildHeaderToFieldMap,
  buildRows,
  createUserSchema,
  normalizeRow,
  parseOrgFunctionsWorkbook,
  readDataSheetRows,
  readWorkbook,
  toUserRowData,
  type OrgFunctionsParseResult,
  type SheetRow,
  type UserInput
} from '@/lib/excel';
import { createDepartmentSchema, type DepartmentInput } from './schemas';
import type { DraftRowShape } from '@/lib/draft-rows/draftRow';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';
import {
  buildUsersUploadHeaderMap,
  isInactiveStatus,
  readPersonioExport,
  type PersonioExportPerson
} from './personioExport';
import { setupDepartmentDrafts, setupTeamDrafts, setupUserDrafts } from './setupRowsToDrafts';

export type ParsedDepartment = DraftRowShape<DepartmentInput>;

export type ParsedUser = DraftRowShape<UserInput>;

export interface DepartmentsParseResult {
  departments: ParsedDepartment[];
  /** The file was Personio's employee export: its rows merge like the Personio read's. */
  personioExport: boolean;
  totalRows: number;
  validRows: number;
  invalidRows: number;
}

export type OrgFunctionsSetupParseResult = OrgFunctionsParseResult & {
  /** The file was Personio's employee export: its memberships take only members, like the Personio read's. */
  personioExport: boolean;
};

export interface UsersParseResult {
  users: ParsedUser[];
  /** People of Personio's employee export the setup cannot take. */
  unsupplied: PersonioSetupUnsuppliedPerson[];
  /** The file was Personio's employee export: it has no Stellenbeschreibung to replace one on screen. */
  personioExport: boolean;
  totalRows: number;
  validRows: number;
  invalidRows: number;
}

/**
 * Map and validate raw department sheet rows. Exported separately from the
 * file-reading shell so the mapping logic is unit-testable.
 */
export function buildDepartmentRows(
  sheetRows: SheetRow[],
  headerToFieldMap: Map<string, string>
): ParsedDepartment[] {
  return buildRows<DepartmentInput>(sheetRows, headerToFieldMap, {
    schema: createDepartmentSchema(),
    toData: (normalized) => ({
      name: normalized.name || '',
      abbreviation: normalized.abbreviation || '',
      description: normalized.description,
      parentAbbr: normalized.parentAbbr,
      managerEmail: normalized.managerEmail || ''
    }),
    isEmpty: (data) => !data.name && !data.abbreviation && !data.managerEmail
  });
}

/**
 * Map and validate raw user sheet rows, leaving out inactive people. Exported
 * separately from the file-reading shell so the mapping logic is unit-testable.
 */
export function buildUserRows(
  sheetRows: SheetRow[],
  headerToFieldMap: Map<string, string>
): ParsedUser[] {
  const activeRows = sheetRows.filter(
    ({ cells }) => !isInactiveStatus(normalizeRow(cells, headerToFieldMap).status)
  );
  return buildRows<UserInput>(activeRows, headerToFieldMap, {
    schema: createUserSchema(),
    toData: toUserRowData,
    isEmpty: (data) => !data.firstName && !data.lastName && !data.email
  });
}

function summarize<T extends { isValid: boolean }>(
  rows: T[]
): { totalRows: number; validRows: number; invalidRows: number } {
  const validRows = rows.filter((row) => row.isValid).length;
  return { totalRows: rows.length, validRows, invalidRows: rows.length - validRows };
}

type PersonioExportSetupRows = Pick<
  ShowPersonioSetupRowsResponse,
  'departments' | 'users' | 'teams' | 'memberships' | 'unsupplied'
>;

/** Each distinct non-empty name by its normalized key, in the spelling it first appears in. */
function distinctNames(names: string[]): Map<string, string> {
  const byKey = new Map<string, string>();
  for (const name of names) {
    if (name && !byKey.has(normalizedKey(name))) byKey.set(normalizedKey(name), name);
  }
  return byKey;
}

/**
 * The setup rows of Personio's employee export, filled as the backend fills
 * them from a Personio read whose departments and teams come from the
 * employees: no parent, lead or manager, a generated abbreviation, and the
 * people without a valid email, department or Stelle listed, not rows.
 */
function personioExportSetupRows(people: PersonioExportPerson[]): PersonioExportSetupRows {
  const contacts = people.map((person) => ({ ...person, email: canonicalEmail(person.email) }));
  const departments = distinctNames(people.map((person) => person.department));
  const teams = distinctNames(people.map((person) => person.team));
  const taken = new Set<string>();

  const users: PersonioExportSetupRows['users'] = [];
  const unsupplied: PersonioSetupUnsuppliedPerson[] = [];
  for (const { firstName, lastName, email, role, ...person } of contacts) {
    const department = departments.get(normalizedKey(person.department));
    if (email && department && role) {
      users.push({ firstName, lastName, email, department, role });
      continue;
    }
    unsupplied.push({
      firstName,
      lastName,
      email,
      reasons: unsuppliedReasons({ email, department, role })
    });
  }

  return {
    departments: [...departments.values()].map((name) => ({
      name,
      abbreviation: claimAbbreviation(abbreviationInitials(name), taken)
    })),
    users,
    teams: [...teams.values()].map((name) => ({ name })),
    memberships: contacts.flatMap(({ email: memberEmail, team }) => {
      const orgFunctionName = teams.get(normalizedKey(team));
      return memberEmail && orgFunctionName ? [{ orgFunctionName, memberEmail }] : [];
    }),
    unsupplied
  };
}

/** The departments of a workbook: its sheet's rows, or the departments of Personio's employee export. */
export function parseDepartmentsWorkbook(workbook: XLSX.WorkBook): DepartmentsParseResult {
  const people = readPersonioExport(workbook);
  if (people) {
    const departments = setupDepartmentDrafts(personioExportSetupRows(people).departments);
    return { departments, personioExport: true, ...summarize(departments) };
  }
  const headerToFieldMap = buildHeaderToFieldMap('departments');
  const departments = buildDepartmentRows(
    readDataSheetRows(workbook, headerToFieldMap),
    headerToFieldMap
  );
  return { departments, personioExport: false, ...summarize(departments) };
}

/** The users of a workbook: its sheet's rows, or the people of Personio's employee export. */
export function parseUsersWorkbook(workbook: XLSX.WorkBook): UsersParseResult {
  const people = readPersonioExport(workbook);
  if (people) {
    const setupRows = personioExportSetupRows(people);
    const users = setupUserDrafts(setupRows.users);
    return { users, unsupplied: setupRows.unsupplied, personioExport: true, ...summarize(users) };
  }
  const headerToFieldMap = buildUsersUploadHeaderMap();
  const users = buildUserRows(readDataSheetRows(workbook, headerToFieldMap), headerToFieldMap);
  return { users, unsupplied: [], personioExport: false, ...summarize(users) };
}

/**
 * The Funktionen or Teams of a workbook; the Teams step also takes the teams
 * and memberships of Personio's employee export.
 */
export function parseOrgFunctionsSetupWorkbook(
  workbook: XLSX.WorkBook,
  kind: OrgFunctionKind
): OrgFunctionsSetupParseResult {
  const people = kind === 'team' ? readPersonioExport(workbook) : undefined;
  if (people) {
    return { ...setupTeamDrafts(personioExportSetupRows(people)), personioExport: true };
  }
  return {
    ...parseOrgFunctionsWorkbook(workbook, kind === 'role' ? 'functions' : 'teams'),
    personioExport: false
  };
}

/**
 * Parse Excel file and validate department data.
 * Accepts columns in any supported language.
 */
export async function parseDepartmentsFromExcel(file: File): Promise<DepartmentsParseResult> {
  return parseDepartmentsWorkbook(await readWorkbook(file));
}

/**
 * Parse Excel file and validate user data.
 * Accepts columns in any supported language.
 */
export async function parseUsersFromExcel(file: File): Promise<UsersParseResult> {
  return parseUsersWorkbook(await readWorkbook(file));
}

export async function parseOrgFunctionsForSetup(
  file: File,
  kind: OrgFunctionKind
): Promise<OrgFunctionsSetupParseResult> {
  return parseOrgFunctionsSetupWorkbook(await readWorkbook(file), kind);
}
